<template>
  <v-container fluid>

    <!-- Page Title -->
    <app-page-title
      v-bind:title="'Photo Gallery'"
      v-bind:icon="'photo_camera'"
    />

    <!-- Loading -->
    <template v-if="isLoading">
      <loading
        :active.sync="isLoading"
        :can-cancel="false"
        loader="dots"
        :color="appColor_Progress"
      />
    </template>
    
    <!-- If: No Photos -->
    <template v-if="sourcesBreedsMedia.length === 0 && !isLoading">
      <app-alert v-bind:alertType="'info'" v-bind:message="'No photos found'" />
    </template>

    <!-- Else: Photos Exist -->
    <template v-else>
      <!-- Filter Toolbar -->
      <v-toolbar dense fixed flat dark :color="appColor_Background" :class="!isSmallDevice ? 'mt-n3 mb-2' : 'mt-n1 mb-2'">
        <v-toolbar-items>
          <!-- Animal Source Dropdown -->
          <v-select
            v-model="selectedSourcePath"
            :items="animalSources"
            @change="selectedBreedPath = sourceBreeds[0].path"
            hide-details
            class="mr-4" />
        </v-toolbar-items>

        <v-spacer />

        <!-- Breed Radios -->
        <v-radio-group v-model="selectedBreedPath" row dark hide-details class="mt-3">
          <template v-for="(breed, i) in sourceBreeds">
            <v-radio :key="i" :label="breed.name" :value="breed.path"></v-radio>
          </template>
        </v-radio-group>
      </v-toolbar>

      <!-- Vue Previewer -->
      <v-card-text :class="isSmallDevice ? 'mt-4' : 'pt-2'">
        <vue-previewer :images="breedPhotos" mode="image">
          <template v-slot:footer="{ image }">{{ image.name }}</template>
        </vue-previewer>
      </v-card-text>
    </template>

    <!-- Error Dialog -->
    <v-dialog v-model="errorDialog" max-width="500px">
      <app-error
        v-bind:errorTitle="errorTitle"
        v-bind:errorText="errorText"
        v-on:close="errorDialog = false"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import appService from '@/app.service'
import AppPageTitle from '@/components/AppPageTitle'
import AppAlert from '@/components/AppAlert'
import AppError from '@/components/AppError'
import { AppColor } from '@/config.js'

export default {
  name: 'PhotoViewer',
  components: {
    AppPageTitle,
    AppAlert,
    AppError
  },
  data () {
    return {
      appColor_Background: AppColor.Background,
      appColor_Progress: AppColor.Progress,

      isLoading: false,
      sourcesBreedsMedia: [],
      animalSources: [],
      selectedSourcePath: null,
      selectedBreedPath: null,
      uri: null,

      // error handling
      isError: false,
      errorDialog: false,
      errorTitle: 'Internal Error',
      errorText: null
    }
  },
  computed: {
    basePath () {
      return `${this.uri}/${this.selectedSourcePath}/${this.selectedBreedPath}`
    },
    sourceBreedsPhotos () {
      if (this.selectedSourcePath == null) return []

      const path = this.selectedSourcePath
      return this.sourcesBreedsMedia.filter(src => { return src.path === path })[0]
    },
    sourceBreeds () {
      if (this.selectedBreedPath == null) return []

      // breeds for the radio buttons
      return this.sourceBreedsPhotos.media
        .map(p => { return {
          path: p.path,
          name: p.breed
        }})
    },
    breedPhotos () {
      if (this.selectedBreedPath == null) return []

      const self = this

      // imageOptions array for the vue-previewer
      return self.sourceBreedsPhotos.media
        .filter(b => {
            return b.path === self.selectedBreedPath })[0].mediaItems
        .map(p => { return {
          src: `${self.basePath}/${p.filename}`,
          thumbnailSrc: self.getThumbnailUrl(p.filename),
          name: p.filename,
          width: '90px',
          height: '90px'
        }})
    },
    isSmallDevice () {
      return this.$vuetify.breakpoint.name === 'xs' || this.$vuetify.breakpoint.name === 'sm'
    }
  },
  methods: {
    loadData() {
      const self = this
      self.isLoading = true

      appService.getSourcesBreedsPhotos().then(data => {
        if (data.errorSeverity > 0) {
          self.isError = true
          self.errorText = data.errorMessage
          self.errorDialog = true
        } else {
          self.uri = data.uri
          self.sourcesBreedsMedia = data.sourcesBreedsMedia

          // construct array for the animal sources dropdown
          self.animalSources = data.sourcesBreedsMedia.map(src => { return {
            value: src.path,
            text: src.source,
          }})

          // default selections
          self.selectedSourcePath = self.animalSources[0].value
          self.selectedBreedPath = self.sourceBreedsPhotos.media[0].path
        }
        self.isLoading = false
      }).catch(() => { self.isLoading = false })        
    },

    getThumbnailUrl (filename) {
      const ext = filename.split('.').pop()
      const url = `${this.basePath}/thumbnail/${filename.replace(ext, 'jpg')}`
      return url
    }    
  },
  created () {
    this.loadData()
  }
}
</script>